import { useState } from 'react';
import { entero, fecha, porcentaje } from '../lib/formato.js';
import {
  CLS_CTR_ESTADO,
  CLS_PRY,
  txt,
  TXT_CTR_ESTADO,
  TXT_CTR_PERIODO,
  TXT_CTR_TIPO,
  TXT_PRY,
} from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Tarifas } from '../piezas/Tarifas.jsx';
import { Formulario, type Campo } from '../piezas/Formulario.jsx';
import { Tabla, type Columna } from '../piezas/Tabla.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Clientes, contratos y proyectos.
 *
 * Las tres son la misma pantalla con otras columnas, asi que se declaran. Lo
 * unico propio de cada una es que decide mostrar: el consumo en contratos, la
 * desviacion contra plan en proyectos.
 */

/* ------------------------------- Clientes -------------------------------- */

interface Cliente extends Record<string, unknown> {
  clave: string;
  nombre: string;
  contacto: string | null;
  correo: string | null;
  rfc: string | null;
  activo: boolean;
}

const CAMPOS_CLIENTE: Campo[] = [
  {
    nombre: 'clave',
    etiqueta: 'Clave',
    tipo: 'texto',
    requerido: true,
    soloAlta: true,
    ayuda: 'cli_ + tres a veinte letras. No se cambia despues.',
    patron: 'cli_[a-z0-9_]{3,20}',
  },
  { nombre: 'nombre', etiqueta: 'Razon social', tipo: 'texto', requerido: true, ancho: true },
  { nombre: 'contacto', etiqueta: 'Contacto', tipo: 'texto' },
  { nombre: 'correo', etiqueta: 'Correo', tipo: 'texto' },
  { nombre: 'rfc', etiqueta: 'RFC', tipo: 'texto', ayuda: 'Doce o trece caracteres.' },
  { nombre: 'activo', etiqueta: 'Activo', tipo: 'si-no' },
];

export function Clientes(): JSX.Element {
  const { puede, veDatosPersonales } = useSesion();
  const [panel, setPanel] = useState<{ fila?: Cliente } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const escribe = puede('MOD_CLIENTES', 'escribir');

  const columnas: Columna<Cliente>[] = [
    {
      campo: 'clave',
      titulo: 'Clave',
      ordenable: true,
      celda: (c) => <span className="folio">{c.clave}</span>,
    },
    { campo: 'nombre', titulo: 'Razon social', ordenable: true },
    { campo: 'contacto', titulo: 'Contacto', secundaria: true },
    { campo: 'correo', titulo: 'Correo', secundaria: true },
    /* El RFC es dato personal: va cifrado en reposo y solo lo reciben los
       perfiles con `ve_datos_personales`. La columna no se dibuja para los
       demas, igual que las de dinero. */
    ...(veDatosPersonales
      ? [{ campo: 'rfc', titulo: 'RFC', secundaria: true } as Columna<Cliente>]
      : []),
    {
      campo: 'activo',
      titulo: 'Estatus',
      celda: (c) => (
        <span className={c.activo ? 'tag t-ok' : 'tag t-mute'}>
          {c.activo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
  ];

  return (
    <>
      <Pagehead />
      <Tabla<Cliente>
        ruta="clientes"
        que="los clientes"
        icono="🏢"
        columnas={columnas}
        llave={(c) => c.clave}
        buscable
        csv
        orden="nombre"
        recarga={recarga}
        {...(escribe ? { alElegir: (fila: Cliente) => setPanel({ fila }) } : {})}
        acciones={
          escribe ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => setPanel({})}
              data-prueba="alta"
            >
              + Cliente
            </button>
          ) : undefined
        }
      />

      {/* Las tarifas viven DENTRO del dialogo del proyecto, no en un
          boton por renglon ni detras del panel.

          La primera version ponia un boton en cada fila y axe la reprobo
          con razon: la fila entera es `role="button"` para abrirse con el
          teclado, asi que un boton adentro es un control anidado. La
          segunda las dibujaba debajo de la tabla, y era peor: el panel es
          `aria-modal="true"`, asi que las tarifas quedaban visibles pero
          inalcanzables —el clic no llegaba y un lector de pantalla
          tampoco—. Aqui, ademas, es donde tienen sentido: la tarifa es
          del proyecto que se esta mirando.

          No se pierde a nadie: los dos perfiles que reciben montos,
          ADMIN y GERENTE, escriben proyectos, asi que ambos llegan. */}
      {panel && (
        <Formulario
          ruta="clientes"
          campos={
            veDatosPersonales ? CAMPOS_CLIENTE : CAMPOS_CLIENTE.filter((c) => c.nombre !== 'rfc')
          }
          titulo={panel.fila ? `Cliente ${panel.fila.clave}` : 'Alta de cliente'}
          actual={panel.fila}
          llave={panel.fila?.clave}
          alCerrar={() => setPanel(null)}
          alGuardar={() => {
            setPanel(null);
            setRecarga(recarga + 1);
          }}
        />
      )}
    </>
  );
}

/* ------------------------------- Contratos -------------------------------- */

interface Contrato extends Record<string, unknown> {
  folio: string;
  cliente: string;
  tipo: string;
  estado: string;
  periodo: string;
  inicio_el: string;
  fin_el: string;
  volumen: number;
  consumido: number | null;
  disponible: number | null;
  agotado: boolean | null;
  proyectos: number;
  moneda: string;
}

const CAMPOS_CONTRATO: Campo[] = [
  {
    nombre: 'cliente_id',
    etiqueta: 'Cliente',
    tipo: 'catalogo',
    catalogo: 'clientes',
    requerido: true,
    soloAlta: true,
    ancho: true,
    ayuda: 'No se cambia: un contrato que cambia de titular es un contrato nuevo.',
  },
  {
    nombre: 'tipo',
    etiqueta: 'Tipo',
    tipo: 'lista',
    requerido: true,
    valores: Object.entries(TXT_CTR_TIPO).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  {
    nombre: 'periodo',
    etiqueta: 'Periodo',
    tipo: 'lista',
    requerido: true,
    valores: Object.entries(TXT_CTR_PERIODO).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  { nombre: 'inicio_el', etiqueta: 'Inicio', tipo: 'fecha', requerido: true },
  { nombre: 'fin_el', etiqueta: 'Fin', tipo: 'fecha', requerido: true },
  {
    nombre: 'volumen',
    etiqueta: 'Volumen contratado',
    tipo: 'numero',
    requerido: true,
    min: 1,
    ayuda: 'Servicios que cubre el contrato.',
  },
  {
    nombre: 'estado',
    etiqueta: 'Estado',
    tipo: 'lista',
    valores: Object.entries(TXT_CTR_ESTADO).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  {
    nombre: 'acumula_saldo',
    etiqueta: 'Acumula saldo',
    tipo: 'si-no',
    ayuda: 'Si lo no consumido de un periodo pasa al siguiente.',
  },
];

export function Contratos(): JSX.Element {
  const { puede } = useSesion();
  const [panel, setPanel] = useState<{ fila?: Contrato } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const escribe = puede('MOD_CONTRATOS', 'escribir');

  const columnas: Columna<Contrato>[] = [
    {
      campo: 'folio',
      titulo: 'Folio',
      ordenable: true,
      celda: (c) => <span className="folio">{c.folio}</span>,
    },
    { campo: 'cliente', titulo: 'Cliente' },
    {
      campo: 'tipo',
      titulo: 'Tipo',
      celda: (c) => (
        <>
          {txt(TXT_CTR_TIPO, c.tipo)}
          <div className="muted" style={{ fontSize: 11 }}>
            {txt(TXT_CTR_PERIODO, c.periodo)}
          </div>
        </>
      ),
    },
    {
      campo: 'estado',
      titulo: 'Estado',
      ordenable: true,
      celda: (c) => (
        <span className={`tag ${CLS_CTR_ESTADO[c.estado] ?? 't-mute'}`}>
          {txt(TXT_CTR_ESTADO, c.estado)}
        </span>
      ),
    },
    {
      campo: 'fin_el',
      titulo: 'Vigencia',
      ordenable: true,
      secundaria: true,
      celda: (c) => (
        <>
          {fecha(c.inicio_el)}
          <div className="muted" style={{ fontSize: 11 }}>
            al {fecha(c.fin_el)}
          </div>
        </>
      ),
    },
    {
      /* Volumen, consumo y saldo en una sola celda con su barra: es la
         pregunta que de verdad se hace de un contrato, y separada en tres
         columnas obliga a hacer la resta a ojo. */
      campo: 'volumen',
      titulo: 'Consumo',
      celda: (c) => <Consumo contrato={c} />,
    },
    {
      campo: 'proyectos',
      titulo: 'Proyectos',
      num: true,
      secundaria: true,
      celda: (c) => entero(c.proyectos),
    },
  ];

  return (
    <>
      <Pagehead />
      <Tabla<Contrato>
        ruta="contratos-doc"
        que="los contratos"
        icono="📄"
        columnas={columnas}
        llave={(c) => c.folio}
        buscable
        csv
        orden="folio"
        recarga={recarga}
        {...(escribe ? { alElegir: (fila: Contrato) => setPanel({ fila }) } : {})}
        acciones={
          escribe ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => setPanel({})}
              data-prueba="alta"
            >
              + Contrato
            </button>
          ) : undefined
        }
      />

      {panel && (
        <Formulario
          ruta="contratos-doc"
          campos={CAMPOS_CONTRATO}
          titulo={panel.fila ? `Contrato ${panel.fila.folio}` : 'Alta de contrato'}
          actual={panel.fila}
          llave={panel.fila?.folio}
          alCerrar={() => setPanel(null)}
          alGuardar={() => {
            setPanel(null);
            setRecarga(recarga + 1);
          }}
        />
      )}
    </>
  );
}

/**
 * Consumo de un contrato.
 *
 * El consumo lo cuenta la base con su propia regla —que desenlace gasta saldo
 * sale de `causales.consume_saldo`— y aqui solo se dibuja. El porcentaje se
 * calcula sobre el volumen porque es lo que se mira; el numero crudo tambien
 * se muestra, porque "87%" de un contrato de 23 servicios no dice cuantos
 * quedan.
 */
function Consumo({ contrato }: { contrato: Contrato }): JSX.Element {
  const consumido = contrato.consumido ?? 0;
  const pct = contrato.volumen > 0 ? Math.min((consumido / contrato.volumen) * 100, 100) : 0;
  const agotado = contrato.agotado === true;
  const apretado = !agotado && pct >= 85;

  return (
    <div style={{ minWidth: 150 }}>
      <div className="fila" style={{ gap: 6, fontSize: 12 }}>
        <b>{entero(consumido)}</b>
        <span className="muted">de {entero(contrato.volumen)}</span>
        <span className="crece" />
        {agotado ? (
          <span className="tag t-hot">agotado</span>
        ) : apretado ? (
          <span className="tag t-warm">{porcentaje(Math.round(pct))}</span>
        ) : (
          <span className="muted">{porcentaje(Math.round(pct))}</span>
        )}
      </div>
      <div className="bar" style={{ marginTop: 4 }}>
        <i
          style={{
            width: `${pct}%`,
            background: agotado ? 'var(--bad)' : apretado ? 'var(--warn)' : 'var(--ok)',
          }}
        />
      </div>
    </div>
  );
}

/* ------------------------------- Proyectos -------------------------------- */

interface Proyecto extends Record<string, unknown> {
  folio: string;
  cliente: string;
  contrato: string | null;
  nombre: string;
  estado: string;
  inicio_el: string;
  fin_el: string;
  gerente: string | null;
  sitios_plan: number | null;
  avance_plan_pct: number | null;
  ventana_inicio: string | null;
  ventana_fin: string | null;
  requiere_inventario: boolean;
  tickets: number;
  tickets_cerrados: number;
}

const CAMPOS_PROYECTO: Campo[] = [
  {
    nombre: 'cliente_id',
    etiqueta: 'Cliente',
    tipo: 'catalogo',
    catalogo: 'clientes',
    requerido: true,
    soloAlta: true,
  },
  {
    nombre: 'contrato_id',
    etiqueta: 'Contrato',
    tipo: 'catalogo',
    catalogo: 'contratos-doc',
    requerido: true,
    ayuda: 'Tiene que ser del mismo cliente y estar vigente.',
  },
  {
    nombre: 'nombre',
    etiqueta: 'Nombre del proyecto',
    tipo: 'texto',
    requerido: true,
    ancho: true,
  },
  {
    nombre: 'estado',
    etiqueta: 'Estado',
    tipo: 'lista',
    valores: Object.entries(TXT_PRY).map(([valor, etiqueta]) => ({ valor, etiqueta })),
  },
  { nombre: 'gerente', etiqueta: 'Gerente', tipo: 'texto' },
  { nombre: 'inicio_el', etiqueta: 'Inicio', tipo: 'fecha', requerido: true },
  { nombre: 'fin_el', etiqueta: 'Fin', tipo: 'fecha', requerido: true },
  { nombre: 'sitios_plan', etiqueta: 'Sitios en el plan', tipo: 'numero', min: 0 },
  {
    nombre: 'avance_plan_pct',
    etiqueta: 'Avance de plan',
    tipo: 'numero',
    min: 0,
    max: 100,
    ayuda: 'En por ciento. Lo captura el gerente; el real lo cuentan los tickets.',
  },
  {
    nombre: 'ventana_inicio',
    etiqueta: 'Ventana desde',
    tipo: 'hora',
    ayuda: 'Si se deja vacia manda la de la ciudad del sitio.',
  },
  { nombre: 'ventana_fin', etiqueta: 'Ventana hasta', tipo: 'hora' },
  {
    nombre: 'requiere_inventario',
    etiqueta: 'Exige captura de inventario',
    tipo: 'si-no',
    ancho: true,
    ayuda: 'Si se exige, un ticket no cierra sin capturar lo instalado y lo retirado.',
  },
];

export function Proyectos(): JSX.Element {
  const { puede, veMontos } = useSesion();
  const [panel, setPanel] = useState<{ fila?: Proyecto } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const escribe = puede('MOD_PROYECTOS', 'escribir');

  const columnas: Columna<Proyecto>[] = [
    {
      campo: 'folio',
      titulo: 'Folio',
      ordenable: true,
      celda: (p) => <span className="folio">{p.folio}</span>,
    },
    {
      campo: 'nombre',
      titulo: 'Proyecto',
      ordenable: true,
      celda: (p) => (
        <>
          {p.nombre}
          <div className="muted" style={{ fontSize: 11 }}>
            {p.cliente}
            {p.contrato && <> · {p.contrato}</>}
          </div>
        </>
      ),
    },
    {
      campo: 'estado',
      titulo: 'Estado',
      ordenable: true,
      celda: (p) => (
        <span className={`tag ${CLS_PRY[p.estado] ?? 't-mute'}`}>{txt(TXT_PRY, p.estado)}</span>
      ),
    },
    { campo: 'gerente', titulo: 'Gerente', secundaria: true },
    {
      campo: 'fin_el',
      titulo: 'Fin',
      ordenable: true,
      secundaria: true,
      celda: (p) => fecha(p.fin_el),
    },
    {
      /* Plan contra real, juntos. Es toda la razon de ser de la columna: la
         desviacion no se ve si hay que abrir dos pantallas para compararla. */
      campo: 'avance_plan_pct',
      titulo: 'Avance plan / real',
      celda: (p) => <Avance proyecto={p} />,
    },
    {
      campo: 'requiere_inventario',
      titulo: 'Inventario',
      secundaria: true,
      celda: (p) =>
        p.requiere_inventario ? (
          <span className="tag t-cold">obligatorio</span>
        ) : (
          <span className="muted">no aplica</span>
        ),
    },
  ];

  return (
    <>
      <Pagehead />
      <Tabla<Proyecto>
        ruta="proyectos"
        que="los proyectos"
        icono="📁"
        columnas={columnas}
        llave={(p) => p.folio}
        buscable
        csv
        orden="folio"
        recarga={recarga}
        {...(escribe ? { alElegir: (fila: Proyecto) => setPanel({ fila }) } : {})}
        acciones={
          escribe ? (
            <button
              type="button"
              className="btn pri"
              onClick={() => setPanel({})}
              data-prueba="alta"
            >
              + Proyecto
            </button>
          ) : undefined
        }
      />

      {panel && (
        <Formulario
          ruta="proyectos"
          campos={CAMPOS_PROYECTO}
          titulo={panel.fila ? `Proyecto ${panel.fila.folio}` : 'Alta de proyecto'}
          {...(panel.fila && veMontos
            ? { extra: <Tarifas folio={panel.fila.folio} puedeEscribir={escribe} /> }
            : {})}
          actual={panel.fila}
          llave={panel.fila?.folio}
          alCerrar={() => setPanel(null)}
          alGuardar={() => {
            setPanel(null);
            setRecarga(recarga + 1);
          }}
        />
      )}
    </>
  );
}

/** Plan contra real, con la desviacion nombrada cuando importa. */
function Avance({ proyecto }: { proyecto: Proyecto }): JSX.Element {
  const plan = proyecto.avance_plan_pct ?? null;
  const real = proyecto.tickets > 0 ? (proyecto.tickets_cerrados / proyecto.tickets) * 100 : null;

  if (plan === null && real === null) {
    return <span className="muted">sin plan ni tickets</span>;
  }

  /* Diez puntos de atraso es el umbral que usa el tablero para marcar riesgo.
     Se nombra en vez de solo colorear: el color no es informacion para todos. */
  const atrasado = plan !== null && real !== null && plan - real >= 10;

  return (
    <div style={{ minWidth: 140 }}>
      <div className="fila" style={{ gap: 6, fontSize: 12 }}>
        <span className="muted">plan</span>
        <b>{plan === null ? '—' : porcentaje(Math.round(plan))}</b>
        <span className="muted">· real</span>
        <b>{real === null ? '—' : porcentaje(Math.round(real))}</b>
      </div>
      {real !== null && (
        <div className="bar" style={{ marginTop: 4 }}>
          <i
            style={{
              width: `${Math.min(real, 100)}%`,
              background: atrasado ? 'var(--bad)' : 'var(--ok)',
            }}
          />
        </div>
      )}
      {atrasado && (
        <span className="tag t-hot" style={{ marginTop: 4 }}>
          atrasado {porcentaje(Math.round((plan ?? 0) - real))}
        </span>
      )}
      {proyecto.tickets > 0 && (
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
          {entero(proyecto.tickets_cerrados)} de {entero(proyecto.tickets)} tickets
        </div>
      )}
    </div>
  );
}
